import { useNuxtApp, useRequestURL } from 'nuxt/app'
import { LocaleCode, normalizeLocaleParam } from '@packages/i18n'
import { useServerReq } from './useServerReq'
import { DEFAULT_COUNTRY_CODE, normalizeCountryCodeValue, useAppStore } from '../stores/app'

type ServerFetchOptions<T = unknown> = NonNullable<Parameters<typeof $fetch<T>>[1]>

export type ServerFetchRequest<T = unknown> = ServerFetchOptions<T> & {
  url: string
  data?: unknown
}

const createBfp = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID().replace(/-/g, '')
  }
  return `${Date.now()}${Math.random().toString(16).slice(2)}`
}

const resolveBasicData = (isWiki: string, cookieHost: string, myCountry: string, bfp: string) => {
  if (isWiki === '1' || cookieHost.includes('wikifx.hk')) {
    return { base: `999,0,1000,999,0,${bfp},0`, basehash: '999,0,2,999,0,0' }
  }
  if (myCountry === '156') {
    return { base: `999,0,1000,999,1,${bfp},1`, basehash: '999,0,1,999,1,1' }
  }
  return { base: `999,0,1000,999,0,${bfp},0`, basehash: '999,0,2,999,0,0' }
}

/** 根据请求来源地址中的语言前缀解析语言；无前缀地址默认使用英文。 */
const resolveLanguageCodeFromSourceUrl = (pathname: string): LocaleCode | null => {
  if (!pathname) return null
  console.log(normalizeLocaleParam(pathname.split(/[/?#]/)[1]), 'xxx')

  return normalizeLocaleParam(pathname.split(/[/?#]/)[1]) ?? LocaleCode.En
}

/** SSR 请求调试记录，仅在路由带 ?p=1 时收集 */
export type SsrRequestRecord = {
  url: string
  method: string
  baseURL?: string
  params?: Record<string, unknown>
  body?: unknown
  headers?: Record<string, string>
  status: 'success' | 'error'
  statusCode?: number
  duration: number
  timestamp: number
  responseBody?: string
  error?: string
}

/** 将响应序列化为完整字符串（不截断，供面板复制使用） */
const stringifyResponse = (data: unknown): string => {
  try {
    return typeof data === 'string' ? data : JSON.stringify(data ?? '', null, 2)
  } catch {
    return '[unserializable]'
  }
}

/** 将 Headers 对象转为普通对象，供 curl 生成使用 */
const headersToRecord = (headers: Headers): Record<string, string> => {
  const record: Record<string, string> = {}
  headers.forEach((value, key) => {
    record[key] = value
  })
  return record
}

/** 检查是否开启 SSR 请求调试（路由 ?p=1），返回调试状态引用或 null */
const getSsrDebugState = () => {
  try {
    if (useRequestURL().searchParams.get('p') !== '1') return null
    return useState<SsrRequestRecord[]>('ssr-request-inspector', () => [])
  } catch {
    return null
  }
}

/**
 * 仅服务端执行的 $fetch 封装
 * - 服务端：直连目标接口
 * - 客户端：抛错（避免静默返回 null 导致状态被覆盖）
 */
export const useServerFetch = async <T = unknown>(config: ServerFetchRequest<T>): Promise<T | null> => {
  if (import.meta.client) {
    throw new Error('useServerFetch() must be called on server side (SSR) only')
  }

  const { url, data, ...options } = config
  const store = useAppStore()
  const req = useServerReq()

  const hostHeader = typeof req?.headers?.host === 'string' ? req.headers.host : ''
  const cookieBfp = useAppCookie('wiki-bfp').value || ''
  const bfp = cookieBfp || store.serverbfp || createBfp()
  const params = (options.params ?? {}) as Record<string, unknown>
  const languageFromParams = typeof params.LanguageCode === 'string' ? params.LanguageCode : ''
  const countryFromParams = normalizeCountryCodeValue(params.CountryCode)
  const countryFromRequest = normalizeCountryCodeValue(req?.headers['country-code'])
  const { base, basehash } = resolveBasicData(
    store.isWiki,
    store.cookieHost || hostHeader,
    normalizeCountryCodeValue(store.myCountry) || countryFromParams || countryFromRequest,
    bfp,
  )

  const mergedHeaders = new Headers(options.headers as HeadersInit | undefined)
  const basicdata1 = mergedHeaders.get('basicdata1')
  const i18nLocale = useNuxtApp().$i18n.locale.value
  const languageFromSourceUrl = resolveLanguageCodeFromSourceUrl(useRequestURL().pathname)
  let languageCode = languageFromSourceUrl || i18nLocale || languageFromParams || store.LanguageCode || LocaleCode.En
  if (['zh-hk', 'zh-tw'].includes(languageCode?.toLowerCase() || '')) {
    languageCode = 'zh'
  }
  const countryCode =
    normalizeCountryCodeValue(store.CountryCode) || countryFromParams || countryFromRequest || DEFAULT_COUNTRY_CODE

  mergedHeaders.set('BasicData', basicdata1 || base)
  mergedHeaders.set('basehash', basehash)
  if (!mergedHeaders.get('LanguageCode') && languageCode) {
    mergedHeaders.set('LanguageCode', languageCode)
  }
  if (!mergedHeaders.get('CountryCode') && countryCode) {
    mergedHeaders.set('CountryCode', countryCode)
  }

  /** 调试模式：收集当前请求信息到 useState，供客户端面板读取 */
  const debugRecords = getSsrDebugState()
  const fetchStartTime = Date.now()

  try {
    const result = await $fetch<T>(url, {
      ...options,
      body: options.body ?? data,
      headers: mergedHeaders,
    } as ServerFetchOptions<T>)

    if (debugRecords) {
      debugRecords.value.push({
        url,
        method: String(options.method || 'GET'),
        baseURL: options.baseURL,
        params: options.params as Record<string, unknown> | undefined,
        body: options.body ?? data,
        headers: headersToRecord(mergedHeaders),
        status: 'success',
        statusCode: 200,
        duration: Date.now() - fetchStartTime,
        timestamp: fetchStartTime,
        responseBody: stringifyResponse(result),
      })
    }

    return result
  } catch (err: any) {
    if (debugRecords) {
      debugRecords.value.push({
        url,
        method: String(options.method || 'GET'),
        baseURL: options.baseURL,
        params: options.params as Record<string, unknown> | undefined,
        body: options.body ?? data,
        headers: headersToRecord(mergedHeaders),
        status: 'error',
        statusCode: err?.response?.status || err?.statusCode,
        duration: Date.now() - fetchStartTime,
        timestamp: fetchStartTime,
        error: err?.message || String(err),
      })
    }
    throw err
  }
}
